// src/components/ui/Toaster.jsx
import React from "react";

const GRADIENT =
  "linear-gradient(135deg,#0B0E13 0%,#8A0F3A 35%,#C11867 70%,#FF2D83 100%)";

export default function Toaster({ theme = "gradient", position = "top-right" }) {
  const [items, setItems] = React.useState([]);

  React.useEffect(() => {
    const mapType = (t) => {
      const s = String(t || "").toLowerCase();
      if (s === "success") return "success";
      if (s === "error" || s === "danger" || s === "fail") return "error";
      return "info";
    };

    const normalize = (detail) => {
      if (!detail) return null;
      const duration = Number.isFinite(detail.duration) ? detail.duration : 2800;

      // A) Old emitter shape: { variant, title, description }
      if (detail.variant || detail.title || detail.description) {
        return {
          id: detail.id || crypto.randomUUID(),
          type: mapType(detail.variant),
          title: detail.title || null,
          description: detail.description || null,
          duration,
        };
      }

      // B) Simple shape: { type, message }
      return {
        id: detail.id || crypto.randomUUID(),
        type: mapType(detail.type),
        title: null,
        description: detail.message || "",
        duration,
      };
    };

    const handler = (e) => {
      const item = normalize(e.detail);
      if (!item) return;
      setItems((prev) => [...prev, item]);
      const t = setTimeout(() => {
        setItems((prev) => prev.filter((x) => x.id !== item.id));
      }, Math.max(1200, item.duration));
      return () => clearTimeout(t);
    };

    // Listen to BOTH event names
    window.addEventListener("toast", handler);
    window.addEventListener("app:toast", handler);
    return () => {
      window.removeEventListener("toast", handler);
      window.removeEventListener("app:toast", handler);
    };
  }, []);

  const pos =
    {
      "top-right": "top-4 right-4",
      "top-left": "top-4 left-4",
      "bottom-right": "bottom-4 right-4",
      "bottom-left": "bottom-4 left-4",
    }[position] || "top-4 right-4";

  return (
    <div className={`pointer-events-none fixed z-[100] ${pos} space-y-3`}>
      {items.map((t) => (
        <ToastCard key={t.id} theme={theme} {...t} />
      ))}
    </div>
  );
}

function ToastCard({ type = "info", title, description, theme }) {
  const icon =
    type === "success" ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    ) : type === "error" ? (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 8h.01M11 12h1v4h-1z" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    );

  const border = type === "error" ? "border-rose-300/50" : "border-white/15";
  const bgClass = theme === "black" ? "bg-black" : "bg-[length:200%_200%]";
  const bgStyle = theme === "black" ? {} : { background: GRADIENT };

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 rounded-2xl px-4 py-3 text-white shadow-lg border ${border} ${bgClass}`}
      style={bgStyle}
      role="status"
      aria-live="polite"
    >
      <span className="mt-[2px] opacity-90">{icon}</span>
      <div className="text-sm leading-5">
        <p className="font-semibold">
          {title ?? (type === "success" ? "Success" : type === "error" ? "Error" : "Notice")}
        </p>
        {description ? <p className="opacity-95">{description}</p> : null}
      </div>
      <span className="ml-2 mt-1 h-2 w-2 rounded-full bg-white/90" />
    </div>
  );
}
